/**
 * Builds AppsFlyer OneLink deep links for a restaurant / grocery — the same
 * long-link shape the client app shares from the merchant screen
 * (resort-app-frontend/src/utils/oneLink.ts). The link opens the merchant in
 * the app when installed (Universal Links / Android App Links) and otherwise
 * redirects to the App Store / Play Store, then deep-links to the merchant
 * after install (deferred deep linking).
 *
 * Resulting shape:
 *   https://<brand>/<template>?af_xp=referral&pid=User_invite&c=<slug>&deep_link_value=rest_id_<id>
 *
 * The brand subdomain + template id differ per environment, matching the
 * client app's env split. `.env*` files are gitignored (CI builds never see
 * them), so the split lives in build-mode defaults, overridable via
 * VITE_ONELINK_BRAND_DOMAIN / VITE_ONELINK_TEMPLATE_ID:
 *   dev/stage builds -> mountly-client-dev.onelink.me / jGVu
 *   production build -> mountly.onelink.me / aDvJ
 */

const IS_PROD_BUILD = import.meta.env.MODE === 'production';

const BRAND_DOMAIN =
  (import.meta.env.VITE_ONELINK_BRAND_DOMAIN && import.meta.env.VITE_ONELINK_BRAND_DOMAIN.trim()) ||
  (IS_PROD_BUILD ? 'mountly.onelink.me' : 'mountly-client-dev.onelink.me');
const TEMPLATE_ID =
  (import.meta.env.VITE_ONELINK_TEMPLATE_ID && import.meta.env.VITE_ONELINK_TEMPLATE_ID.trim()) ||
  (IS_PROD_BUILD ? 'aDvJ' : 'jGVu');

// Attribution values — kept identical to the client app's share links so the
// links behave the same in the AppsFlyer dashboard.
const SHARE_EXPERIENCE = 'referral'; // af_xp
const SHARE_PID = 'User_invite'; // media source

// deep_link_value prefixes understood by the client app's deeplink router
// (resort-app-frontend/src/utils/deeplinkRouting.ts).
export const REST_DEEPLINK_PREFIX = 'rest_id_';
export const GROCERY_DEEPLINK_PREFIX = 'grocery_id_';

const slugify = (value?: string | null): string =>
  (value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

const buildOneLink = (deepLinkValue: string, campaign: string): string => {
  const query = [
    `af_xp=${encodeURIComponent(SHARE_EXPERIENCE)}`,
    `pid=${encodeURIComponent(SHARE_PID)}`,
    `c=${encodeURIComponent(campaign)}`,
    `deep_link_value=${encodeURIComponent(deepLinkValue)}`,
  ].join('&');
  return `https://${BRAND_DOMAIN}/${TEMPLATE_ID}?${query}`;
};

export const buildRestaurantDeepLink = ({
  restaurantId,
  restaurantName,
}: {
  restaurantId: string;
  restaurantName?: string | null;
}): string =>
  buildOneLink(`${REST_DEEPLINK_PREFIX}${restaurantId}`, slugify(restaurantName) || restaurantId);

export const buildGroceryDeepLink = ({
  groceryId,
  groceryName,
}: {
  groceryId: string;
  groceryName?: string | null;
}): string =>
  buildOneLink(`${GROCERY_DEEPLINK_PREFIX}${groceryId}`, slugify(groceryName) || groceryId);
